import { useEffect, useRef, useState, useCallback } from 'react';

const COLORS = ['#e53935', '#fb8c00', '#fdd835', '#43a047', '#1e88e5', '#8e24aa', '#000000', '#ffffff'];

/**
 * Full-screen editor to scribble (freehand) and add text on a captured
 * screenshot. Keeps an ordered list of ops so Undo/Clear can redraw, and
 * exports the flattened result as a data URL on Save.
 */
const ScreenshotAnnotator = ({ imageDataUrl, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const opsRef = useRef([]);        // committed ops: {type:'stroke', color, width, points:[{x,y}]} | {type:'text', color, size, x, y, text}
  const drawingRef = useRef(null);  // in-progress stroke
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#e53935');
  const [ready, setReady] = useState(false);
  const [, force] = useState(0);    // re-render for toolbar disabled states

  const lineWidth = () => {
    const c = canvasRef.current;
    return c ? Math.max(3, Math.round(c.width / 400)) : 3;
  };
  const fontSize = () => {
    const c = canvasRef.current;
    return c ? Math.max(16, Math.round(c.width / 55)) : 18;
  };

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const all = drawingRef.current ? [...opsRef.current, drawingRef.current] : opsRef.current;
    all.forEach(op => {
      if (op.type === 'stroke') {
        if (op.points.length < 1) return;
        ctx.strokeStyle = op.color;
        ctx.lineWidth = op.width;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.beginPath();
        op.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.stroke();
      } else if (op.type === 'text') {
        ctx.font = `bold ${op.size}px system-ui, sans-serif`;
        ctx.textBaseline = 'top';
        // outline for legibility on any background
        ctx.lineWidth = Math.max(2, op.size / 8);
        ctx.strokeStyle = op.color === '#ffffff' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.8)';
        ctx.strokeText(op.text, op.x, op.y);
        ctx.fillStyle = op.color;
        ctx.fillText(op.text, op.x, op.y);
      }
    });
  }, []);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      setReady(true);
      redraw();
    };
    img.src = imageDataUrl;
  }, [imageDataUrl, redraw]);

  const toCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const onPointerDown = (e) => {
    if (!ready) return;
    const pt = toCanvasCoords(e);
    if (tool === 'text') {
      const text = window.prompt('Enter text for the annotation:');
      if (text && text.trim()) {
        opsRef.current.push({ type: 'text', color, size: fontSize(), x: pt.x, y: pt.y, text: text.trim() });
        redraw();
        force(n => n + 1);
      }
      return;
    }
    // pen
    e.preventDefault();
    drawingRef.current = { type: 'stroke', color, width: lineWidth(), points: [pt] };
    redraw();
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current.points.push(toCanvasCoords(e));
    redraw();
  };

  const endStroke = () => {
    if (drawingRef.current) {
      if (drawingRef.current.points.length > 0) opsRef.current.push(drawingRef.current);
      drawingRef.current = null;
      redraw();
      force(n => n + 1);
    }
  };

  const undo = () => { opsRef.current.pop(); redraw(); force(n => n + 1); };
  const clearAll = () => { opsRef.current = []; drawingRef.current = null; redraw(); force(n => n + 1); };

  const save = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // JPEG keeps the payload small for full-page screenshots.
    onSave(canvas.toDataURL('image/jpeg', 0.9));
  };

  return (
    <div className="shot-annot-overlay">
      <div className="shot-annot-toolbar">
        <div className="shot-annot-tools">
          <button
            type="button"
            className={`shot-tool ${tool === 'pen' ? 'active' : ''}`}
            onClick={() => setTool('pen')}
            title="Draw"
          >✏️ Draw</button>
          <button
            type="button"
            className={`shot-tool ${tool === 'text' ? 'active' : ''}`}
            onClick={() => setTool('text')}
            title="Add text"
          >🅃 Text</button>
        </div>
        <div className="shot-annot-colors">
          {COLORS.map(c => (
            <button
              key={c}
              type="button"
              className={`shot-color ${color === c ? 'active' : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
              aria-label={`color ${c}`}
            />
          ))}
        </div>
        <div className="shot-annot-actions">
          <button type="button" className="bug-btn-secondary" onClick={undo} disabled={opsRef.current.length === 0}>Undo</button>
          <button type="button" className="bug-btn-secondary" onClick={clearAll} disabled={opsRef.current.length === 0}>Clear</button>
          <button type="button" className="bug-btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="bug-btn-primary" onClick={save} disabled={!ready}>Attach to report</button>
        </div>
      </div>
      <div className="shot-annot-stage">
        <canvas
          ref={canvasRef}
          className={`shot-annot-canvas tool-${tool}`}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={endStroke}
          onMouseLeave={endStroke}
        />
      </div>
      <div className="shot-annot-hint">
        {tool === 'text' ? 'Click on the image to place text.' : 'Click and drag to draw. Use Undo to remove the last mark.'}
      </div>
    </div>
  );
};

export default ScreenshotAnnotator;
